import type {
  AcceptInvitationResult,
  ChangeMemberRoleInput,
  CreateCustomRoleInput,
  CreateTeamInput,
  CustomRoleProjection,
  InvitationProjection,
  InviteMemberInput,
  MemberProjection,
  TeamProjection,
  TeamRoleListItem,
  TeamSettingsProjection,
  TeamSummaryProjection,
  TransferOwnershipInput,
  UpdateCustomRoleInput,
  UpdateTeamInput,
  UpdateTeamSettingsInput,
} from '@projoint/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, idempotencyKey } from './client';

// ── Queries ────────────────────────────────────────────────────────────────

export function useTeams() {
  return useQuery({
    queryKey: ['teams'],
    queryFn: () => api<TeamSummaryProjection[]>('/api/teams'),
    staleTime: 60 * 1000,
  });
}

export function useTeam(slug: string | undefined) {
  return useQuery({
    queryKey: ['teams', slug],
    queryFn: () => api<TeamProjection>(`/api/teams/${slug}`),
    enabled: !!slug,
    staleTime: 30 * 1000,
  });
}

export function useTeamMembers(slug: string | undefined) {
  return useQuery({
    queryKey: ['teams', slug, 'members'],
    queryFn: () => api<MemberProjection[]>(`/api/teams/${slug}/members`),
    enabled: !!slug,
    staleTime: 30 * 1000,
  });
}

export function useTeamSettings(slug: string | undefined) {
  return useQuery({
    queryKey: ['teams', slug, 'settings'],
    queryFn: () => api<TeamSettingsProjection>(`/api/teams/${slug}/settings`),
    enabled: !!slug,
    staleTime: 30 * 1000,
  });
}

export function useTeamInvitations(slug: string | undefined) {
  return useQuery({
    queryKey: ['teams', slug, 'invitations'],
    queryFn: () => api<InvitationProjection[]>(`/api/teams/${slug}/invitations`),
    enabled: !!slug,
    staleTime: 30 * 1000,
  });
}

export function useTeamRoles(slug: string | undefined) {
  return useQuery({
    queryKey: ['teams', slug, 'roles'],
    queryFn: () => api<TeamRoleListItem[]>(`/api/teams/${slug}/roles`),
    enabled: !!slug,
    staleTime: 30 * 1000,
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────

export function useCreateTeam() {
  const qc = useQueryClient();
  return useMutation({
    // Both callers (mobile create-team-sheet, desktop create-team-modal) show
    // their own error toast, so opt out of the global MutationCache handler to
    // avoid a duplicate stacked toast on failure.
    meta: { silentErrors: true },
    mutationFn: (input: CreateTeamInput) =>
      api<TeamProjection>('/api/teams', {
        method: 'POST',
        body: input,
        idempotencyKey: idempotencyKey(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['teams'] });
    },
  });
}

export function useUpdateTeam(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: UpdateTeamInput) =>
      api<TeamProjection>(`/api/teams/${slug}`, { method: 'PATCH', body: patch }),
    onSuccess: (team) => {
      qc.setQueryData(['teams', slug], team);
      void qc.invalidateQueries({ queryKey: ['teams'] });
    },
  });
}

export function useDeleteTeam(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (confirmSlug: string) =>
      api<void>(`/api/teams/${slug}`, { method: 'DELETE', body: { confirmSlug } }),
    onSuccess: () => {
      qc.removeQueries({ queryKey: ['teams', slug] });
      void qc.invalidateQueries({ queryKey: ['teams'] });
    },
  });
}

/**
 * Leave a team — self-removal.  Server rejects an OWNER (must transfer first)
 * and personal teams (owner is the user).  On success the team vanishes from
 * the user's list, so we drop its cached entry and refetch the list.
 */
export function useLeaveTeam(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>(`/api/teams/${slug}/leave`, { method: 'POST' }),
    onSuccess: () => {
      qc.removeQueries({ queryKey: ['teams', slug] });
      void qc.invalidateQueries({ queryKey: ['teams'] });
    },
  });
}

export function useTransferOwnership(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TransferOwnershipInput) =>
      api<void>(`/api/teams/${slug}/transfer-ownership`, { method: 'POST', body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['teams', slug] });
    },
  });
}

export function useUpdateTeamSettings(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: UpdateTeamSettingsInput) =>
      api<TeamSettingsProjection>(`/api/teams/${slug}/settings`, {
        method: 'PATCH',
        body: patch,
      }),
    onSuccess: (settings) => {
      qc.setQueryData(['teams', slug, 'settings'], settings);
    },
  });
}

export function useInviteMember(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    // Both callers (mobile invite-member-sheet, desktop invite-member-dialog)
    // render their own error toast — including a specific "verify your email
    // first" case — so suppress the global handler to avoid a duplicate toast.
    meta: { silentErrors: true },
    mutationFn: (input: InviteMemberInput) =>
      api<InvitationProjection>(`/api/teams/${slug}/members/invite`, {
        method: 'POST',
        body: input,
        idempotencyKey: idempotencyKey(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['teams', slug, 'invitations'] });
    },
  });
}

export function useChangeMemberRole(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, ...input }: ChangeMemberRoleInput & { userId: string }) =>
      api<MemberProjection>(`/api/teams/${slug}/members/${userId}`, {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['teams', slug, 'members'] });
    },
  });
}

export function useRemoveMember(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api<void>(`/api/teams/${slug}/members/${userId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['teams', slug, 'members'] });
      // DB CASCADE wipes the user's project_access rows in this team — any
      // open Edit Project → Members tab would otherwise keep showing them
      // until refresh.  Invalidating every project-scoped access query under
      // this team forces a refetch on the next render.
      void qc.invalidateQueries({ queryKey: ['teams', slug, 'projects'] });
    },
  });
}

export function useRevokeInvitation(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      api<void>(`/api/teams/${slug}/invitations/${invitationId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['teams', slug, 'invitations'] });
    },
  });
}

export function useCreateCustomRole(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCustomRoleInput) =>
      api<CustomRoleProjection>(`/api/teams/${slug}/roles`, {
        method: 'POST',
        body: input,
        idempotencyKey: idempotencyKey(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['teams', slug, 'roles'] });
    },
  });
}

export function useUpdateCustomRole(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ roleId, ...patch }: UpdateCustomRoleInput & { roleId: string }) =>
      api<CustomRoleProjection>(`/api/teams/${slug}/roles/${roleId}`, {
        method: 'PATCH',
        body: patch,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['teams', slug, 'roles'] });
    },
  });
}

export function useDeleteCustomRole(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (roleId: string) =>
      api<void>(`/api/teams/${slug}/roles/${roleId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['teams', slug, 'roles'] });
    },
  });
}

// ── Invitation acceptance (token-based, no slug context) ───────────────────

export function useAcceptInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (token: string) =>
      api<AcceptInvitationResult>(`/api/invites/${token}/accept`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['teams'] });
    },
  });
}

export function useDeclineInvitation() {
  return useMutation({
    mutationFn: (token: string) =>
      api<void>(`/api/invites/${token}/decline`, { method: 'POST' }),
  });
}

/**
 * Pre-accept preview.  Backend is public so the email recipient can
 * see what they're being invited to before clicking Accept (which still
 * requires authentication + email-match).
 */
export interface InvitationPreviewProjection {
  teamName: string;
  teamSlug: string;
  teamMemberCount: number;
  inviterName: string;
  inviterAvatarUrl: string | null;
  invitedEmail: string;
  rolePreset: string | null;
  customRoleName: string | null;
  expiresAt: string;
  acceptedAt: string | null;
  declinedAt: string | null;
}

export function useInvitationPreview(token: string | undefined) {
  return useQuery({
    queryKey: ['invitations', 'preview', token],
    queryFn: () => api<InvitationPreviewProjection>(`/api/invites/${token}/preview`),
    enabled: !!token,
    retry: false,
  });
}

// ── In-app invitation handling (by id — from the `team_invitation` inbox
//    notification, which carries the invitation id, not the email token). ──

export function useInvitationByIdPreview(invitationId: string | undefined) {
  return useQuery({
    queryKey: ['invitations', 'preview-by-id', invitationId],
    queryFn: () => api<InvitationPreviewProjection>(`/api/invites/by-id/${invitationId}/preview`),
    enabled: !!invitationId,
    retry: false,
  });
}

export function useAcceptInvitationById() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      api<AcceptInvitationResult>(`/api/invites/by-id/${invitationId}/accept`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['teams'] });
      void qc.invalidateQueries({ queryKey: ['inbox'] });
    },
  });
}

export function useDeclineInvitationById() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (invitationId: string) =>
      api<void>(`/api/invites/by-id/${invitationId}/decline`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inbox'] });
    },
  });
}
